"use client"

import { useState } from "react"
import { ChevronRight, Loader2 } from "lucide-react"
import { useAdminPartnersQuery } from "@/hooks/queries/partners"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { NameWithBadges } from "@/components/partners/name-with-badges"
import { formatMicroUsd } from "@/lib/billing/utils/format"
import { PARTNER_ACCOUNT_STATUSES } from "@/lib/partners/constants"
import { formatPercent } from "@/lib/partners/percent"
import { AdminPartnerDetail } from "@/components/partners/admin/admin-partner-detail"
import { useTranslations } from "next-intl"




export function AdminPartnersTab() {


  const tUi = useTranslations("Partners.PartnersAdminAdminPartnersTab")

  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null)
  const partners = useAdminPartnersQuery()

  if (selectedPartnerId) {
    return <AdminPartnerDetail partnerId={selectedPartnerId} onBack={() => setSelectedPartnerId(null)} />
  }

  if (partners.isError) {
    return <Alert variant="destructive"><AlertDescription><span key={tUi("unableToLoadPartnersPleaseRefreshAndTry")}>{tUi("unableToLoadPartnersPleaseRefreshAndTry")}</span></AlertDescription></Alert>
  }

  if (partners.isPending) {
    return (
      <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingPartners")}>{tUi("loadingPartners")}</span></div>
    )
  }

  if (!partners.data.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><span key={tUi("noPartnersYet")}>{tUi("noPartnersYet")}</span></CardTitle>
          <CardDescription><span key={tUi("tenantsJoinTheProgramThemselvesFromTheirPartner")}>{tUi("tenantsJoinTheProgramThemselvesFromTheirPartner")}</span></CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table className="min-w-0">
          <TableHeader>
            <TableRow>
              <TableHead><span key={tUi("partner")}>{tUi("partner")}</span></TableHead>
              <TableHead className="text-right"><span key={tUi("commission")}>{tUi("commission")}</span></TableHead>
              <TableHead className="text-right"><span key={tUi("balance")}>{tUi("balance")}</span></TableHead>
              <TableHead className="text-right"><span key={tUi("referred")}>{tUi("referred")}</span></TableHead>
              <TableHead className="text-right"><span key={tUi("earned")}>{tUi("earned")}</span></TableHead>
              <TableHead className="text-right"><span key={tUi("clientBonuses")}>{tUi("clientBonuses")}</span></TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.data.map(partner => (
              <TableRow key={partner.partner_id}>
                <TableCell className="sm:min-w-48">
                  <NameWithBadges name={partner.tenant_name}>
                    {partner.status === PARTNER_ACCOUNT_STATUSES.SUSPENDED && (
                      <Badge variant="destructive"><span key={tUi("suspended")}>{tUi("suspended")}</span></Badge>
                    )}
                    {!partner.program_enabled && <Badge variant="secondary"><span key={tUi("programOff")}>{tUi("programOff")}</span></Badge>}
                  </NameWithBadges>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <span>{<span key={tUi("value0", { value0: String(formatPercent(partner.commission_percent)) })}>{tUi("value0", { value0: String(formatPercent(partner.commission_percent)) })}</span>}</span>
                  {partner.commission_percent_override === null && (
                    <span className="ml-1 text-xs text-muted-foreground"><span key={tUi("default")}>{tUi("default")}</span></span>
                  )}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">{formatMicroUsd(partner.balance_microusd)}</TableCell>
                <TableCell className="text-right">{partner.referred_count}</TableCell>
                <TableCell className="text-right whitespace-nowrap">{formatMicroUsd(partner.lifetime_earned_microusd)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">{formatMicroUsd(partner.lifetime_client_bonus_microusd)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedPartnerId(partner.partner_id)}>
                    <span key={tUi("open")}>{tUi("open")}</span><ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
